/**
 * 视口灯光预设：HDR IBL + 辅助光参数，供 Three 视口与 UI 共用。
 */

/** 与磁盘工程、Pinia 对齐的灯光预设 id */
export const LIGHTING_PRESET_IDS = ['classic', 'studio', 'warm', 'cool', 'sunset'] as const

export type LightingPresetId = (typeof LIGHTING_PRESET_IDS)[number]

/** 新建工程与迁移缺省时的默认预设 */
export const DEFAULT_LIGHTING_PRESET_ID: LightingPresetId = 'classic'

/**
 * 单条灯光预设定义。
 */
export type LightingPresetDefinition = {
  id: LightingPresetId
  /** 简短中文名，供 `title` / 无障碍 */
  label: string
  /**
   * 相对站点根路径的 HDR；`null` 且 {@link useRoomEnvironment} 为真时表示用程序化 RoomEnvironment。
   */
  envMapUrl: string | null
  /** 经典光：Three.js 编辑器默认 RoomEnvironment（非 HDR 文件） */
  useRoomEnvironment?: boolean
  /** {@link THREE.WebGLRenderer#toneMappingExposure} */
  toneMappingExposure: number
  /** {@link THREE.Scene#environmentIntensity}，无 environment 时由视口忽略 */
  environmentIntensity: number
  /** 环境绕 Y 轴旋转（弧度），影响反射主方向 */
  environmentRotationY: number
  /** 半球光强度系数（相对 {@link LIGHTING_BASE_HEMISPHERE}） */
  hemisphereIntensity: number
  /** 平行光强度系数（相对 {@link LIGHTING_BASE_DIRECTIONAL}） */
  directionalIntensity: number
  /** 平行光位置（世界空间，不必单位化） */
  directionalPosition: { x: number; y: number; z: number }
  /** 材质球缩略图：锥形渐变用色（仅 UI） */
  spherePreviewCss: string
}

/** 半球光强度基准 */
export const LIGHTING_BASE_HEMISPHERE = 1.24
/** 平行光强度基准 */
export const LIGHTING_BASE_DIRECTIONAL = 1.45

/** 经典光：对齐 Three.js 编辑器默认半球光（天空 / 地面 / 强度） */
export const CLASSIC_EDITOR_HEMISPHERE_SKY = 0xffffff
export const CLASSIC_EDITOR_HEMISPHERE_GROUND = 0x444444
/** 经典光：对齐 Three.js 编辑器默认填充光强度（半球光 + 平行光均为 1） */
export const CLASSIC_EDITOR_FILL_LIGHT_INTENSITY = 1

/**
 * 视口「环境填充」倍率默认值：作用于半球光强度与 `scene.environmentIntensity`（IBL 漫反射环境亮度）。
 */
export const VIEWPORT_AMBIENT_INTENSITY_SCALE_DEFAULT = 1.35

/** {@link clampViewportAmbientIntensityScale} 下限 */
export const VIEWPORT_AMBIENT_INTENSITY_SCALE_MIN = 0.25

/** {@link clampViewportAmbientIntensityScale} 上限 */
export const VIEWPORT_AMBIENT_INTENSITY_SCALE_MAX = 3.5

/**
 * 将任意输入规范为合法的环境光倍率。
 *
 * @param raw - 磁盘或 UI 读入的数值
 */
export function clampViewportAmbientIntensityScale(raw: unknown): number {
  const n = Number(raw)
  if (!Number.isFinite(n)) return VIEWPORT_AMBIENT_INTENSITY_SCALE_DEFAULT
  return Math.min(
    VIEWPORT_AMBIENT_INTENSITY_SCALE_MAX,
    Math.max(VIEWPORT_AMBIENT_INTENSITY_SCALE_MIN, n),
  )
}

const BASE_HEMI = LIGHTING_BASE_HEMISPHERE
const BASE_DIR = LIGHTING_BASE_DIRECTIONAL
/** 编辑器静态资源基路径（开发环境通常为 `/editor/`） */
const ASSET_BASE_URL = import.meta.env.BASE_URL

/**
 * 全部灯光预设（顺序即 UI 展示顺序）。
 */
export const LIGHTING_PRESET_LIST: readonly LightingPresetDefinition[] = [
  {
    id: 'classic',
    label: '经典光',
    envMapUrl: null,
    useRoomEnvironment: true,
    /** 对齐 `three.js-187/examples/test2.html`（ACES Filmic + RoomEnvironment）观感 */
    toneMappingExposure: 1.05,
    /** 对齐 `test2.html`：`scene.environmentIntensity = 1.25`，让浅色金属背板（如 iPhone Air）正常反光，而非整片发黑 */
    environmentIntensity: 1.25,
    environmentRotationY: 0,
    /** 与 {@link CLASSIC_EDITOR_FILL_LIGHT_INTENSITY} 一致；视口对 classic 不再叠加强度基准与环境倍率 */
    hemisphereIntensity: 1,
    directionalIntensity: 1,
    directionalPosition: { x: 3.5, y: 7.5, z: 5 },
    spherePreviewCss:
      'conic-gradient(from 200deg, #e8e8ec 0deg, #b8bcc4 120deg, #f5f5f7 240deg, #c8ccd4 360deg)',
  },
  {
    id: 'studio',
    label: '中性棚拍',
    envMapUrl: `${ASSET_BASE_URL}env/pedestrian_overpass_1k.hdr`,
    toneMappingExposure: 1.56,
    environmentIntensity: 1.38,
    environmentRotationY: 0.35,
    hemisphereIntensity: 0.68,
    directionalIntensity: 0.62,
    directionalPosition: { x: 4, y: 6, z: 3.5 },
    spherePreviewCss:
      'conic-gradient(from 160deg, #f2f4f8 0deg, #9aa3b2 100deg, #dfe4ec 220deg, #aeb6c4 360deg)',
  },
  {
    id: 'warm',
    label: '暖色晨光',
    envMapUrl: `${ASSET_BASE_URL}env/spruit_sunrise_1k.hdr`,
    toneMappingExposure: 1.6,
    environmentIntensity: 1.4,
    environmentRotationY: -0.25,
    hemisphereIntensity: 0.65,
    directionalIntensity: 0.58,
    directionalPosition: { x: 5, y: 4, z: 2.5 },
    spherePreviewCss:
      'conic-gradient(from 220deg, #fff4e0 0deg, #f0a868 110deg, #ffd8a8 240deg, #e8b878 360deg)',
  },
  {
    id: 'cool',
    label: '冷色夜景',
    envMapUrl: `${ASSET_BASE_URL}env/moonless_golf_1k.hdr`,
    toneMappingExposure: 1.68,
    environmentIntensity: 1.45,
    environmentRotationY: 0.5,
    hemisphereIntensity: 0.62,
    directionalIntensity: 0.54,
    directionalPosition: { x: -3, y: 5, z: 6 },
    spherePreviewCss:
      'conic-gradient(from 180deg, #dce8f4 0deg, #6b8cb8 120deg, #a8c0e0 260deg, #8898b8 360deg)',
  },
  {
    id: 'sunset',
    label: '黄昏氛围',
    envMapUrl: `${ASSET_BASE_URL}env/venice_sunset_1k.hdr`,
    toneMappingExposure: 1.58,
    environmentIntensity: 1.35,
    environmentRotationY: 0.15,
    hemisphereIntensity: 0.66,
    directionalIntensity: 0.56,
    directionalPosition: { x: 6, y: 3, z: -2 },
    spherePreviewCss:
      'conic-gradient(from 240deg, #ffe8d0 0deg, #e87850 100deg, #c05078 220deg, #f8c090 360deg)',
  },
] as const

/**
 * @param id - 任意字符串
 * @returns 是否为已知灯光预设 id
 */
export function isLightingPresetId(id: string): id is LightingPresetId {
  return (LIGHTING_PRESET_IDS as readonly string[]).includes(id)
}

/**
 * @param id - 预设 id
 * @returns 定义；未知 id 时回退到 {@link DEFAULT_LIGHTING_PRESET_ID}
 */
export function getLightingPresetDefinition(id: string): LightingPresetDefinition {
  const found = LIGHTING_PRESET_LIST.find((p) => p.id === id)
  return found ?? LIGHTING_PRESET_LIST.find((p) => p.id === DEFAULT_LIGHTING_PRESET_ID)!
}

/**
 * 将预设中的强度系数转为实际半球光强度。
 *
 * @param def - 预设
 */
export function resolvedHemisphereIntensity(def: LightingPresetDefinition): number {
  return BASE_HEMI * def.hemisphereIntensity
}

/**
 * 将预设中的强度系数转为实际平行光强度。
 *
 * @param def - 预设
 */
export function resolvedDirectionalIntensity(def: LightingPresetDefinition): number {
  return BASE_DIR * def.directionalIntensity
}
